import { useState, useEffect, useCallback } from 'react';
import {
  ShoppingCart, Plus, Search, X, Check, AlertCircle, Loader2,
  RefreshCw, Trash2, Ban, CheckCircle, Truck, Package, Clock,
  ArrowUpRight
} from 'lucide-react';
import salesOrderController from '../../controllers/salesOrderController';

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  Pending:    { label: 'Pending',    color: '#92400e', bg: '#fef3c7', icon: Clock },
  Processing: { label: 'Processing', color: '#1d4ed8', bg: '#dbeafe', icon: Package },
  Shipped:    { label: 'Shipped',    color: '#6d28d9', bg: '#ede9fe', icon: Truck },
  Delivered:  { label: 'Delivered',  color: '#15803d', bg: '#dcfce7', icon: CheckCircle },
  Cancelled:  { label: 'Cancelled',  color: '#b91c1c', bg: '#fef2f2', icon: Ban },
};

const PAYMENT_CONFIG = {
  Unpaid:  { label: 'Unpaid',  color: '#b91c1c', bg: '#fef2f2' },
  Partial: { label: 'Partial', color: '#92400e', bg: '#fef3c7' },
  Paid:    { label: 'Paid',    color: '#15803d', bg: '#dcfce7' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const Spinner = ({ size = 16 }) => <Loader2 size={size} style={{ display: 'inline', animation: 'spin 1s linear infinite' }} />;

const Badge = ({ status, config = STATUS_CONFIG }) => {
  const cfg = config[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: cfg.bg, color: cfg.color }}>
      {Icon && <Icon size={10} />}{cfg.label}
    </span>
  );
};

const Banner = ({ type, message, onClose }) => (
  <div style={{ background: type === 'error' ? '#fef2f2' : '#f0fdf4', border: `1px solid ${type === 'error' ? '#fca5a5' : '#86efac'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, color: type === 'error' ? '#b91c1c' : '#15803d', fontSize: 13 }}>
    {type === 'error' ? <AlertCircle size={14} /> : <Check size={14} />}
    <span style={{ flex: 1 }}>{message}</span>
    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'flex' }}><X size={13} /></button>
  </div>
);

// ─── Create/Edit Modal ─────────────────────────────────────────────────────────
const FormModal = ({ order, onSave, onClose, isSaving }) => {
  const isEdit = !!order;
  const [form, setForm] = useState({
    customerName: order?.customerName || '', email: order?.email || '', phone: order?.phone || '',
    company: order?.company || '', deliveryDate: order?.deliveryDate?.split('T')[0] || '',
    paymentStatus: order?.paymentStatus || 'Unpaid', totalAmount: order?.totalAmount || 0, notes: order?.notes || '',
  });
  const f = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  return (
    <div style={ms.overlay}>
      <div style={{ ...ms.box, maxWidth: 560 }}>
        <div style={ms.head}>
          <h3 style={ms.title}>{isEdit ? 'Edit Sales Order' : 'Create Sales Order'}</h3>
          <button onClick={onClose} style={ms.closeBtn}><X size={16} /></button>
        </div>
        <div style={ms.body}>
          <div style={ms.row2}>
            <div style={ms.fg}><label style={ms.lbl}>Customer Name *</label><input style={ms.inp} value={form.customerName} onChange={(e) => f('customerName', e.target.value)} /></div>
            <div style={ms.fg}><label style={ms.lbl}>Email *</label><input style={ms.inp} type="email" value={form.email} onChange={(e) => f('email', e.target.value)} /></div>
            <div style={ms.fg}><label style={ms.lbl}>Phone *</label><input style={ms.inp} value={form.phone} onChange={(e) => f('phone', e.target.value)} /></div>
            <div style={ms.fg}><label style={ms.lbl}>Company</label><input style={ms.inp} value={form.company} onChange={(e) => f('company', e.target.value)} /></div>
            <div style={ms.fg}><label style={ms.lbl}>Delivery Date</label><input style={ms.inp} type="date" value={form.deliveryDate} onChange={(e) => f('deliveryDate', e.target.value)} /></div>
            <div style={ms.fg}><label style={ms.lbl}>Payment Status</label>
              <select style={ms.inp} value={form.paymentStatus} onChange={(e) => f('paymentStatus', e.target.value)}>
                <option value="Unpaid">Unpaid</option><option value="Partial">Partial</option><option value="Paid">Paid</option>
              </select>
            </div>
            <div style={ms.fg}><label style={ms.lbl}>Total Amount (₹) *</label><input style={ms.inp} type="number" min="0" value={form.totalAmount} onChange={(e) => f('totalAmount', e.target.value)} /></div>
          </div>
          <div style={ms.fg}><label style={ms.lbl}>Notes</label><textarea style={{ ...ms.inp, height: 60, resize: 'vertical' }} value={form.notes} onChange={(e) => f('notes', e.target.value)} /></div>
        </div>
        <div style={ms.foot}>
          <button onClick={onClose} style={ms.ghostBtn}>Cancel</button>
          <button onClick={() => onSave(form)} disabled={isSaving || !form.customerName} style={{ ...ms.primaryBtn, opacity: (isSaving || !form.customerName) ? 0.6 : 1 }}>
            {isSaving ? <><Spinner size={13} /> Saving…</> : (isEdit ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Convert to Invoice Modal ────────────────────────────────────────────────
const ConvertModal = ({ order, onConfirm, onClose, isSaving }) => (
  <div style={ms.overlay}>
    <div style={{ ...ms.box, maxWidth: 420 }}>
      <div style={ms.head}>
        <h3 style={ms.title}>Convert to Invoice</h3>
        <button onClick={onClose} style={ms.closeBtn}><X size={16} /></button>
      </div>
      <div style={ms.body}>
        <p style={{ fontSize: 13, color: '#475569', marginBottom: 4, lineHeight: 1.5 }}>
          Generate an invoice for <strong>{order?.orderId}</strong> ({order?.customerName})?
        </p>
        <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#64748b' }}>Amount</span><span style={{ fontWeight: 700 }}>{fmt(order?.totalAmount)}</span>
          </div>
        </div>
      </div>
      <div style={ms.foot}>
        <button onClick={onClose} style={ms.ghostBtn}>Cancel</button>
        <button onClick={onConfirm} disabled={isSaving} style={{ ...ms.primaryBtn, background: '#15803d' }}>
          {isSaving ? <><Spinner size={13} /> Converting…</> : <><ArrowUpRight size={13} style={{ display: 'inline', marginRight: 5 }} />Convert</>}
        </button>
      </div>
    </div>
  </div>
);

// ─── Cancel Modal ─────────────────────────────────────────────────────────────
const CancelModal = ({ order, onConfirm, onClose, isSaving }) => {
  const [reason, setReason] = useState('');
  return (
    <div style={ms.overlay}>
      <div style={{ ...ms.box, maxWidth: 420 }}>
        <div style={ms.head}>
          <h3 style={ms.title}>Cancel Order</h3>
          <button onClick={onClose} style={ms.closeBtn}><X size={16} /></button>
        </div>
        <div style={ms.body}>
          <p style={{ fontSize: 13, color: '#475569', marginBottom: 14, lineHeight: 1.5 }}>
            Are you sure you want to cancel <strong>{order?.orderId}</strong>?
          </p>
          <div style={ms.fg}><label style={ms.lbl}>Reason (optional)</label><textarea style={{ ...ms.inp, height: 60, resize: 'vertical' }} value={reason} onChange={(e) => setReason(e.target.value)} /></div>
        </div>
        <div style={ms.foot}>
          <button onClick={onClose} style={ms.ghostBtn}>Keep Order</button>
          <button onClick={() => onConfirm(reason)} disabled={isSaving} style={{ ...ms.primaryBtn, background: '#ef4444' }}>
            {isSaving ? <><Spinner size={13} /> Cancelling…</> : <><Ban size={13} style={{ display: 'inline', marginRight: 5 }} />Cancel</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Detail Drawer ────────────────────────────────────────────────────────────
const OrderDetail = ({ order, onClose, onAction, actionLoading, isSuperAdmin }) => {
  if (!order) return null;
  const nextStatusMap = { Pending: 'Processing', Processing: 'Shipped', Shipped: 'Delivered' };
  const nextStatus = nextStatusMap[order.status];
  const canConvert = order.status === 'Delivered';
  const canCancel  = !['Delivered', 'Cancelled'].includes(order.status);
  const canDelete  = order.status === 'Pending' && isSuperAdmin;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 480, background: '#fff', height: '100%', overflowY: 'auto', boxShadow: '-4px 0 20px rgba(0,0,0,.12)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>{order.orderId}</h2>
            <div style={{ display: 'flex', gap: 6 }}>
              <Badge status={order.status} />
              <Badge status={order.paymentStatus} config={PAYMENT_CONFIG} />
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={15} /></button>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
            <div style={dr.row}><span style={dr.lbl}>Customer</span><span style={dr.val}>{order.customerName}</span></div>
            <div style={dr.row}><span style={dr.lbl}>Email</span><span style={dr.val}>{order.email}</span></div>
            <div style={dr.row}><span style={dr.lbl}>Phone</span><span style={dr.val}>{order.phone}</span></div>
            {order.company && <div style={dr.row}><span style={dr.lbl}>Company</span><span style={dr.val}>{order.company}</span></div>}
            <div style={{ ...dr.row, fontWeight: 700, fontSize: 15, borderTop: '1px solid #e2e8f0', paddingTop: 8, marginTop: 4 }}>
              <span>Amount</span><span>{fmt(order.totalAmount)}</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={dr.infoCard}><span style={dr.lbl}>Order Date</span><span style={dr.val}>{fmtDate(order.orderDate)}</span></div>
            <div style={dr.infoCard}><span style={dr.lbl}>Delivery Date</span><span style={dr.val}>{fmtDate(order.deliveryDate)}</span></div>
          </div>

          {order.notes && (
            <div style={dr.section}>
              <div style={dr.sectionTitle}>Notes</div>
              <p style={{ fontSize: 13, color: '#475569', margin: 0 }}>{order.notes}</p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {nextStatus && (
              <button disabled={actionLoading.status} onClick={() => onAction('status', order, nextStatus)} style={{ ...dr.actionBtn, background: '#1d4ed8', color: '#fff' }}>
                {actionLoading.status ? <Spinner size={13} /> : <Truck size={13} />} Mark as {nextStatus}
              </button>
            )}
            {canConvert && (
              <button disabled={actionLoading.convert} onClick={() => onAction('convert', order)} style={{ ...dr.actionBtn, background: '#15803d', color: '#fff' }}>
                {actionLoading.convert ? <Spinner size={13} /> : <ArrowUpRight size={13} />} Convert to Invoice
              </button>
            )}
            {canCancel && (
              <button disabled={actionLoading.cancel} onClick={() => onAction('cancel', order)} style={{ ...dr.actionBtn, background: '#fff', color: '#ef4444', border: '1px solid #fca5a5' }}>
                {actionLoading.cancel ? <Spinner size={13} /> : <Ban size={13} />} Cancel Order
              </button>
            )}
            {canDelete && (
              <button disabled={actionLoading.delete} onClick={() => onAction('delete', order)} style={{ ...dr.actionBtn, background: '#fff', color: '#64748b', border: '1px solid #e2e8f0' }}>
                {actionLoading.delete ? <Spinner size={13} /> : <Trash2 size={13} />} Delete (pending only)
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const SalesOrdersPage = () => {
  const [orders,        setOrders]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [banner,        setBanner]        = useState(null);
  const [filters,       setFilters]       = useState({ status: '', page: 1, limit: 12, sortBy: 'createdAt', sortOrder: 'desc' });
  const [search,        setSearch]        = useState('');
  const [selected,      setSelected]      = useState(null);
  const [modal,         setModal]         = useState(null); // 'create' | 'edit' | 'convert' | 'cancel'
  const [modalOrder,    setModalOrder]    = useState(null);
  const [isSaving,      setIsSaving]      = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const isSuperAdmin = salesOrderController.isSuperAdmin();

  const notify = useCallback((type, message) => {
    setBanner({ type, message });
    setTimeout(() => setBanner(null), 4000);
  }, []);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const activeFilters = { ...filters };
      if (search) activeFilters.search = search;
      const res = await salesOrderController.getAllOrders(activeFilters);
      setOrders(res.success ? res.data : []);
      if (!res.success) notify('error', res.message);
    } catch (err) {
      notify('error', 'Failed to load orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, search, notify]);

  useEffect(() => { loadData(false); }, [loadData]);

  const setFilter = (k, v) => setFilters((p) => ({ ...p, [k]: v, page: 1 }));
  const setActLoading = (k, v) => setActionLoading((p) => ({ ...p, [k]: v }));

  const handleAction = (type, order, extra) => {
    if (type === 'status') { handleStatusChange(order, extra); return; }
    setModalOrder(order);
    setModal(type);
    if (selected) setSelected(null);
  };

  const handleStatusChange = async (order, status) => {
    setActLoading('status', true);
    const res = await salesOrderController.updateStatus(order.id || order._id, status);
    setActLoading('status', false);
    if (res.success) { notify('success', res.message); loadData(true); } else notify('error', res.message);
  };

  const handleSave = async (formData) => {
    setIsSaving(true);
    const isEdit = !!modalOrder;
    const res = isEdit
      ? await salesOrderController.updateOrder(modalOrder.id || modalOrder._id, formData)
      : await salesOrderController.createOrder(formData);
    setIsSaving(false);
    if (res.success) { notify('success', res.message); setModal(null); loadData(true); } else notify('error', res.message);
  };

  const handleConvert = async () => {
    setIsSaving(true);
    const res = await salesOrderController.convertToInvoice(modalOrder.id || modalOrder._id, {
      customerName: modalOrder.customerName, email: modalOrder.email, phone: modalOrder.phone,
      company: modalOrder.company, totalAmount: modalOrder.totalAmount,
    });
    setIsSaving(false);
    if (res.success) { notify('success', res.message); setModal(null); loadData(true); } else notify('error', res.message);
  };

  const handleCancel = async (reason) => {
    setIsSaving(true);
    const res = await salesOrderController.cancelOrder(modalOrder.id || modalOrder._id, reason);
    setIsSaving(false);
    if (res.success) { notify('success', res.message); setModal(null); loadData(true); } else notify('error', res.message);
  };

  const handleDelete = async (order) => {
    setActLoading('delete', true);
    const res = await salesOrderController.deleteOrder(order.id || order._id);
    setActLoading('delete', false);
    if (res.success) { notify('success', res.message); setSelected(null); loadData(true); } else notify('error', res.message);
  };

  const handleDetailAction = (type, order, extra) => {
    if (type === 'delete') handleDelete(order);
    else handleAction(type, order, extra);
  };

  const stats = {
    total:      orders.length,
    pending:    orders.filter((o) => o.status === 'Pending').length,
    processing: orders.filter((o) => o.status === 'Processing').length,
    delivered:  orders.filter((o) => o.status === 'Delivered').length,
  };

  return (
    <div style={{ padding: 24, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShoppingCart size={22} color="#3b82f6" />Sales Orders
          </h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Track orders from confirmation through delivery</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => loadData(true)} disabled={refreshing}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', color: '#475569' }}>
            <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />Refresh
          </button>
          <button onClick={() => { setModal('create'); setModalOrder(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', background: '#1e293b', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#fff' }}>
            <Plus size={14} />Create Order
          </button>
        </div>
      </div>

      {banner && <Banner type={banner.type} message={banner.message} onClose={() => setBanner(null)} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Orders', value: stats.total,      color: '#1e293b' },
          { label: 'Pending',      value: stats.pending,    color: '#92400e' },
          { label: 'Processing',   value: stats.processing, color: '#1d4ed8' },
          { label: 'Delivered',    value: stats.delivered,  color: '#15803d' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', border: '1px solid #f1f5f9' }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, fontWeight: 500 }}>{s.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadData(false)}
            placeholder="Search orders..."
            style={{ width: '100%', padding: '8px 8px 8px 32px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
        </div>
        <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', outline: 'none' }}>
          {[['', 'All Status'], ['Pending', 'Pending'], ['Processing', 'Processing'], ['Shipped', 'Shipped'], ['Delivered', 'Delivered'], ['Cancelled', 'Cancelled']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, gap: 10, color: '#64748b' }}>
          <Spinner size={28} /><span style={{ fontSize: 14 }}>Loading orders…</span>
        </div>
      ) : orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <ShoppingCart size={40} color="#e2e8f0" style={{ marginBottom: 12 }} />
          <p style={{ fontSize: 14, margin: 0 }}>No orders found</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #f1f5f9', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                  {['Order #', 'Customer', 'Delivery Date', 'Amount', 'Payment', 'Status', ''].map((h) => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id || o._id} style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    onClick={() => setSelected(o)}>
                    <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>{o.orderId}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontWeight: 500 }}>{o.customerName}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{o.company || ''}</div>
                    </td>
                    <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>{fmtDate(o.deliveryDate)}</td>
                    <td style={{ padding: '11px 14px', fontWeight: 600 }}>{fmt(o.totalAmount)}</td>
                    <td style={{ padding: '11px 14px' }}><Badge status={o.paymentStatus} config={PAYMENT_CONFIG} /></td>
                    <td style={{ padding: '11px 14px' }}><Badge status={o.status} /></td>
                    <td style={{ padding: '11px 14px' }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && (
        <OrderDetail order={selected} onClose={() => setSelected(null)} onAction={handleDetailAction} actionLoading={actionLoading} isSuperAdmin={isSuperAdmin} />
      )}

      {(modal === 'create' || modal === 'edit') && (
        <FormModal order={modalOrder} onSave={handleSave} onClose={() => setModal(null)} isSaving={isSaving} />
      )}
      {modal === 'convert' && modalOrder && (
        <ConvertModal order={modalOrder} onConfirm={handleConvert} onClose={() => setModal(null)} isSaving={isSaving} />
      )}
      {modal === 'cancel' && modalOrder && (
        <CancelModal order={modalOrder} onConfirm={handleCancel} onClose={() => setModal(null)} isSaving={isSaving} />
      )}
    </div>
  );
};

const ms = {
  overlay:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1010, padding: 16 },
  box:        { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 580, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.2)' },
  head:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f1f5f9' },
  title:      { fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 },
  closeBtn:   { background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' },
  body:       { padding: '16px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 },
  foot:       { padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#fafafa' },
  row2:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  fg:         { display: 'flex', flexDirection: 'column', gap: 4 },
  lbl:        { fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px' },
  inp:        { padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, color: '#1e293b', outline: 'none', background: '#fff', width: '100%', boxSizing: 'border-box' },
  ghostBtn:   { padding: '8px 16px', background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#475569' },
  primaryBtn: { padding: '8px 18px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 },
};

const dr = {
  row:       { display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13 },
  lbl:       { color: '#64748b' },
  val:       { fontWeight: 500, color: '#1e293b' },
  infoCard:  { background: '#f8fafc', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 3 },
  section:   { background: '#f8fafc', borderRadius: 8, padding: '12px 14px' },
  sectionTitle: { fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 },
  actionBtn: { width: '100%', padding: '10px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 },
};

export default SalesOrdersPage;